/**
 * The editor's behaviour, as ProseMirror plugins: undo, Word's keys (Ctrl+B, Ctrl+I, Ctrl+U,
 * Tab in a list...), typing shortcuts (`- ` a list, `1. ` a numbered one, `# ` a heading,
 * `> ` a quote, `[[Name]]` a mention), tables, a block id for every top-level block, and the
 * print layout: the gaps drawn between sheets where {@link paginate} says they break.
 */
import { baseKeymap, chainCommands, exitCode, setBlockType, toggleMark } from "prosemirror-commands";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { history, redo, undo } from "prosemirror-history";
import { InputRule, inputRules, textblockTypeInputRule, wrappingInputRule } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { liftListItem, sinkListItem, splitListItem } from "prosemirror-schema-list";
import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { columnResizing, goToNextCell, tableEditing } from "prosemirror-tables";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import { docSchema, freshId, missingIds, paginate, samePagination, SHEET_GAP, type Pagination, type Sheet } from "../../../state/documents";

const { nodes, marks } = docSchema;

/** Word's keys. */
function keys(): Plugin[] {
  const item = nodes.list_item!;
  return [
    keymap({
      "Mod-z": undo,
      "Shift-Mod-z": redo,
      "Mod-y": redo,
      "Mod-b": toggleMark(marks.strong!),
      "Mod-i": toggleMark(marks.em!),
      "Mod-u": toggleMark(marks.underline!),
      "Mod-Alt-0": setBlockType(nodes.paragraph!),
      "Mod-Alt-1": setBlockType(nodes.heading!, { level: 1 }),
      "Mod-Alt-2": setBlockType(nodes.heading!, { level: 2 }),
      "Mod-Alt-3": setBlockType(nodes.heading!, { level: 3 }),
      "Shift-Enter": chainCommands(exitCode, (state, dispatch) => {
        if (dispatch) dispatch(state.tr.replaceSelectionWith(nodes.hard_break!.create()).scrollIntoView());
        return true;
      }),
      "Mod-Enter": (state, dispatch) => {
        if (dispatch) dispatch(state.tr.replaceSelectionWith(nodes.page_break!.create({ id: freshId() })).scrollIntoView());
        return true;
      },
      Enter: splitListItem(item),
      Tab: chainCommands(goToNextCell(1), sinkListItem(item)),
      "Shift-Tab": chainCommands(goToNextCell(-1), liftListItem(item)),
    }),
    keymap(baseKeymap),
  ];
}

/** Typing shortcuts. */
function shortcuts(): Plugin {
  return inputRules({
    rules: [
      wrappingInputRule(/^\s*([-+*])\s$/, nodes.bullet_list!),
      wrappingInputRule(/^(\d+)\.\s$/, nodes.ordered_list!, (match) => ({ order: Number(match[1]) }), (match, node) => node.childCount + Number(node.attrs.order) === Number(match[1])),
      wrappingInputRule(/^\s*>\s$/, nodes.blockquote!),
      textblockTypeInputRule(/^(#{1,3})\s$/, nodes.heading!, (match) => ({ level: match[1]!.length })),
      new InputRule(/\[\[([^\]\n]+)\]\]$/, (state, match, start, end) => state.tr.replaceWith(start, end, nodes.mention!.create({ name: match[1]!.trim() }))),
    ],
  });
}

/** Gives every top-level block an id of its own (a block split in two, a pasted one). */
function ids(): Plugin {
  return new Plugin({
    appendTransaction(transactions, _old, state) {
      if (!transactions.some((t) => t.docChanged)) return null;
      const missing = missingIds(state.doc);
      if (!missing.length) return null;
      const tr = state.tr;
      for (const pos of missing) tr.setNodeAttribute(pos, "id", freshId());
      return tr.setMeta("addToHistory", false);
    },
  });
}

/** The print layout's state. */
export const LAYOUT_KEY = new PluginKey<Pagination>("print-layout");

/** The height of the gap drawn where a sheet ends with `filler` blank pixels left on it. */
function gapHeight(sheet: Sheet, filler: number): number {
  return filler + sheet.margins[2] + SHEET_GAP + sheet.margins[0];
}

/** The gap between two sheets: the rest of the first with its page number, the desk, the next one's top margin. */
function gapDom(sheet: Sheet, filler: number, page: number): HTMLElement {
  const gap = document.createElement("div");
  gap.className = "doc-gap";
  gap.contentEditable = "false";
  gap.style.height = `${gapHeight(sheet, filler)}px`;
  gap.style.marginLeft = `-${sheet.margins[3]}px`;
  gap.style.marginRight = `-${sheet.margins[1]}px`;
  const rest = document.createElement("div");
  rest.className = "doc-gap-rest";
  rest.style.height = `${filler + sheet.margins[2]}px`;
  const number = document.createElement("span");
  number.className = "doc-page-number";
  number.textContent = String(page);
  rest.append(number);
  const desk = document.createElement("div");
  desk.className = "doc-gap-desk";
  desk.style.height = `${SHEET_GAP}px`;
  gap.append(rest, desk);
  return gap;
}

/** The start of each top-level block of `state`'s document. */
function blockStarts(state: EditorState): number[] {
  const out: number[] = [];
  state.doc.forEach((_node, offset) => out.push(offset));
  return out;
}

/**
 * The print layout: measures the blocks as drawn (gaps left out), works out where the sheets
 * break, draws the gaps there, and tells `laidOut` (the sheets and the room left on the last).
 */
function printLayout(sheet: () => Sheet, laidOut: (p: Pagination) => void): Plugin {
  return new Plugin<Pagination>({
    key: LAYOUT_KEY,
    state: {
      init: () => ({ breaks: [], sheets: 1, tail: 0 }),
      apply: (tr, value) => tr.getMeta(LAYOUT_KEY) ?? value,
    },
    props: {
      decorations(state) {
        const p = LAYOUT_KEY.getState(state);
        if (!p?.breaks.length && !p?.lifts) return DecorationSet.empty;
        const starts = blockStarts(state);
        const s = sheet();
        const widgets = p.breaks.flatMap((b, n) => {
          const at = starts[b.before];
          if (at === undefined) return [];
          const filler = Math.round(b.filler);
          return [Decoration.widget(at, () => gapDom(s, filler, n + 1), { side: -1, key: `gap-${n}-${b.before}-${filler}-${s.width}x${s.height}`, ignoreSelection: true })];
        });
        // Pictures kept on their sheet: drawn lower by `--keep` (`picture.ts`, `document.css`), their place unchanged.
        const lowered = Object.entries(p.lifts ?? {}).flatMap(([index, by]) => {
          const at = starts[Number(index)];
          const node = at === undefined ? null : state.doc.nodeAt(at);
          return node ? [Decoration.node(at!, at! + node.nodeSize, { class: "doc-kept", style: `--keep: ${by}px` })] : [];
        });
        return DecorationSet.create(state.doc, [...widgets, ...lowered]);
      },
    },
    view(view) {
      let frame = 0;
      const measure = () => {
        frame = 0;
        if (!view.dom.isConnected) return;
        const s = sheet();
        const current = LAYOUT_KEY.getState(view.state) ?? { breaks: [], sheets: 1, tail: 0 };
        const gapBefore = new Map(current.breaks.map((b) => [b.before, gapHeight(s, Math.round(b.filler))]));
        const blocks: { top: number; height: number; breakBefore: boolean; reach?: number; rise?: number }[] = [];
        let shift = 0;
        let i = 0;
        view.state.doc.forEach((node, offset) => {
          shift += gapBefore.get(i) ?? 0;
          const dom = view.nodeDOM(offset);
          const el = dom instanceof HTMLElement ? dom : null;
          // A picture may be drawn past its block (beside the words, in front or behind): how far.
          const frame = node.type === nodes.image ? el?.querySelector(".doc-image-frame") : null;
          const drawn = frame?.getBoundingClientRect();
          const own = el?.getBoundingClientRect();
          // Measured where it is placed: without the lowering this layout itself asked for.
          const keep = el ? parseFloat(el.style.getPropertyValue("--keep")) || 0 : 0;
          const reach = drawn && own ? drawn.bottom - keep - own.top : undefined;
          const rise = drawn && own ? Math.max(0, own.top - (drawn.top - keep)) : undefined;
          blocks.push({ top: el ? el.offsetTop - shift : 0, height: el?.offsetHeight ?? 0, breakBefore: node.type === nodes.page_break, reach, rise });
          i++;
        });
        // Each block's room: down to the next one's top (its margins with it).
        const measured = blocks.map((b, n) => ({ top: b.top, extent: n + 1 < blocks.length ? Math.max(b.height, blocks[n + 1]!.top - b.top) : b.height, breakBefore: n > 0 && blocks[n - 1]!.breakBefore, reach: b.reach, rise: b.rise }));
        const next = paginate(measured, s.content, s.margins[0]);
        laidOut(next);
        if (!samePagination(next, current)) view.dispatch(view.state.tr.setMeta(LAYOUT_KEY, next).setMeta("addToHistory", false));
      };
      const later = () => {
        if (!frame) frame = requestAnimationFrame(measure);
      };
      later();
      const resized = new ResizeObserver(later);
      resized.observe(view.dom);
      return {
        update: later,
        destroy() {
          if (frame) cancelAnimationFrame(frame);
          resized.disconnect();
        },
      };
    },
  });
}

/** Asks the print layout to measure again (the paper changed). */
export function relayout(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(LAYOUT_KEY, { breaks: [], sheets: 1, tail: 0 }).setMeta("addToHistory", false));
}

/** Every plugin of the editor. */
export function editorPlugins(sheet: () => Sheet, laidOut: (p: Pagination) => void): Plugin[] {
  return [shortcuts(), ...keys(), history(), dropCursor(), gapCursor(), columnResizing(), tableEditing(), ids(), printLayout(sheet, laidOut)];
}
